import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Clock, MapPin, CheckCircle2 } from "lucide-react";
import { api, errMsg, FIELD, AREA } from "@/lib/api";
import { CONTACT, FAQS } from "@/lib/site";
import { PageHero, Reveal, SectionHeading } from "@/components/common/Reveal";
import { InstagramBlock } from "@/components/common/InstagramBlock";
import { Field } from "@/components/common/Field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const EMPTY = { full_name: "", email: "", phone: "", subject: "", message: "" };
const INFO = [[Mail, "Email", CONTACT.email, CONTACT.emailHref], [Phone, "Phone", CONTACT.phone, CONTACT.phoneHref], [Clock, "Hours", CONTACT.hours], [MapPin, "Location", CONTACT.location]];

const ContactForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", f);
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) return (
    <div className="lux-card p-12 text-center h-full flex flex-col justify-center" data-testid="contact-success">
      <CheckCircle2 className="w-12 h-12 text-[#D4AF37] mx-auto" />
      <h3 className="mt-5 text-3xl font-medium">Message received.</h3>
      <p className="mt-3 text-white/60">We'll be in touch soon.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="lux-card p-8 md:p-10 grid sm:grid-cols-2 gap-6" data-testid="contact-form">
      <Field label="Full Name" required><Input className={FIELD} required value={f.full_name} onChange={set("full_name")} data-testid="contact-name-input" /></Field>
      <Field label="Email" required><Input className={FIELD} type="email" required value={f.email} onChange={set("email")} data-testid="contact-email-input" /></Field>
      <Field label="Phone"><Input className={FIELD} value={f.phone} onChange={set("phone")} data-testid="contact-phone-input" /></Field>
      <Field label="Subject" required><Input className={FIELD} required value={f.subject} onChange={set("subject")} data-testid="contact-subject-input" /></Field>
      <Field label="Message" required className="sm:col-span-2"><Textarea className={AREA} required minLength={5} value={f.message} onChange={set("message")} data-testid="contact-message-input" /></Field>
      <p className="sm:col-span-2 text-xs text-white/45">Please don't include SSNs or other sensitive financial details in this form.</p>
      <div className="sm:col-span-2"><button className="btn-gold" disabled={busy} data-testid="contact-submit-button">{busy ? "Sending…" : "Send Message"}</button></div>
    </form>
  );
};

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <PageHero testid="contact-hero" eyebrow="Contact" title={<>Let's build your <span className="italic gold-text">strategy.</span></>}
        sub="Questions about taxes, business or your financial goals? Reach out — simply good people are ready to help." />
      <section className="bg-[#0A0A0A] pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 space-y-4" data-testid="contact-info">
            {INFO.map(([Icon, l, v, href], i) => (
              <Reveal key={l} delay={i * 0.06}>
                <div className="lux-card p-6 flex gap-4 items-start"><Icon className="w-5 h-5 text-[#D4AF37] mt-1" /><div className="min-w-0"><p className="text-xs tracking-[0.2em] uppercase text-white/45">{l}</p>{href ? <a href={href} className="mt-1 block text-white/85 hover:text-[#D4AF37] transition-colors break-all" data-testid={`contact-${l.toLowerCase()}-link`}>{v}</a> : <p className="mt-1 text-white/85">{v}</p>}</div></div>
              </Reveal>
            ))}
          </div>
          <div className="lg:col-span-8"><ContactForm /></div>
        </div>
      </section>
      <section className="bg-black py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <SectionHeading eyebrow="FAQs" title="Frequently asked questions" className="mb-12" />
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {FAQS.map((x, i) => (
              <AccordionItem key={x.q} value={`f${i}`} className="lux-card px-6 border-white/10">
                <AccordionTrigger className="text-left text-base md:text-lg hover:no-underline hover:text-[#D4AF37]" data-testid={`faq-trigger-${i}`}>{x.q}</AccordionTrigger>
                <AccordionContent className="text-white/65 leading-relaxed">{x.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
      <InstagramBlock />
    </div>
  );
}
